import axios from "axios";
import PropTypes from "prop-types";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import OutlinedInput from "@mui/material/OutlinedInput";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";

import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

import LoginLayout from "layouts/authentication/components/LoginLayout";
import { ACCENT } from "layouts/authentication/constants/loginTheme";

import { signInWithFirebaseToken } from "authService";
import { bootstrapAuthenticatedSession, waitForFirebaseTokenSync } from "authBootstrap";
import { useArgonController } from "context";
import { useLanguage } from "context/LanguageContext";

const PLACEHOLDER_LIGHT = "#ced4da";
const PLACEHOLDER_DARK = "#adb5bd";

function buildOutlinedInputSx(darkMode) {
  const ph = darkMode ? PLACEHOLDER_DARK : PLACEHOLDER_LIGHT;
  const placeholderRules = {
    color: `${ph} !important`,
    opacity: "1 !important",
  };
  return {
    width: "100%",
    height: 48,
    borderRadius: "8px",
    fontSize: "0.95rem",
    alignItems: "center",
    bgcolor: darkMode ? "rgba(255,255,255,0.04)" : "#f8fafc",
    color: darkMode ? "rgba(255,255,255,0.88)" : "rgba(15,23,42,0.88)",
    transition: "background-color 0.2s ease",
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: darkMode ? "rgba(255,255,255,0.09)" : "rgba(15,23,42,0.14)",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: darkMode ? "rgba(255,255,255,0.18)" : "rgba(15,23,42,0.28)",
    },
    "&.Mui-focused": {
      bgcolor: darkMode ? "rgba(30,134,246,0.06)" : "#ffffff",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderWidth: "1.5px",
      borderColor: ACCENT,
    },
    "& input": {
      px: 2,
    },
    "& input::placeholder": placeholderRules,
    "& input::-webkit-input-placeholder": placeholderRules,
    "& input::-moz-placeholder": placeholderRules,
  };
}

function FieldLabel({ children, error, darkMode }) {
  const theme = useTheme();
  return (
    <Typography
      component="label"
      variant="caption"
      sx={{
        display: "block",
        mb: 0.75,
        fontWeight: 600,
        fontSize: "0.8rem",
        letterSpacing: "0.01em",
        color: error ? theme.palette.error.main : darkMode ? "rgba(255,255,255,0.65)" : "rgba(15,23,42,0.72)",
        userSelect: "none",
      }}
    >
      {children}
    </Typography>
  );
}

FieldLabel.propTypes = {
  children: PropTypes.node.isRequired,
  error: PropTypes.bool,
  darkMode: PropTypes.bool.isRequired,
};

function SignIn() {
  const theme = useTheme();
  const [controller] = useArgonController();
  const { darkMode } = controller;
  const { t } = useLanguage();

  const [documento, setDocumento] = useState("");
  const [contraseña, setContraseña] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState("");
  const [docError, setDocError] = useState(false);
  const [passError, setPassError] = useState(false);

  const navigate = useNavigate();

  const inputSx = buildOutlinedInputSx(darkMode);
  const errMain = theme.palette.error.main;

  const iconColor = darkMode ? "rgba(255,255,255,0.3)" : "rgba(15,23,42,0.38)";
  const iconHover = darkMode ? "rgba(255,255,255,0.6)" : "rgba(15,23,42,0.65)";

  const titleColor = darkMode ? "rgba(255,255,255,0.93)" : "rgba(10,18,36,0.92)";
  const subtitleColor = darkMode ? "rgba(255,255,255,0.38)" : "rgba(15,23,42,0.48)";
  const supportMuted = darkMode ? "rgba(255,255,255,0.28)" : "rgba(15,23,42,0.38)";
  const supportLink = darkMode ? "rgba(96,180,255,0.85)" : ACCENT;

  const toggleShowPassword = () => setShowPassword(!showPassword);

  const handleLogin = async (event) => {
    event.preventDefault();
    setFormError("");
    setDocError(false);
    setPassError(false);

    if (!documento.trim()) {
      setDocError(true);
      setFormError(t("login.error.emptyEmail"));
      return;
    }
    if (!contraseña) {
      setPassError(true);
      setFormError(t("login.error.emptyPassword"));
      return;
    }

    setLoading(true);

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_BASE_URL || process.env.REACT_APP_API_URL;
      if (!backendUrl || backendUrl === "undefined") {
        throw new Error(
          "Falta configurar la URL del backend (REACT_APP_BACKEND_BASE_URL o REACT_APP_API_URL) en el despliegue."
        );
      }
      const loginEndpoint = `${String(backendUrl).replace(/\/$/, "")}/login`;

      let loginResponse;
      try {
        loginResponse = await axios.post(
          loginEndpoint,
          {
            documento,
            contraseña,
          },
          {
            timeout: 60000,
            withCredentials: false,
            headers: { "Content-Type": "application/json" },
            validateStatus(status) {
              return status < 500;
            },
          }
        );
      } catch (axiosError) {
        console.error("❌ [SignIn] Error de red al conectar con el backend:", axiosError);
        if (axiosError.response) {
          console.error(
            "❌ [SignIn] Respuesta del servidor:",
            axiosError.response.status,
            axiosError.response.data
          );
          throw new Error(
            `Error del servidor: ${axiosError.response.status}. ${axiosError.response.data?.error || axiosError.response.data?.message || "Error desconocido"}`
          );
        } else if (axiosError.request) {
          console.error("❌ [SignIn] No se recibió respuesta del servidor", {
            code: axiosError.code,
            message: axiosError.message,
            endpoint: loginEndpoint,
          });

          if (axiosError.code === "ECONNABORTED") {
            throw new Error(
              "El servidor tardó demasiado en responder. Inténtalo de nuevo o comprueba si el API está lento."
            );
          }

          let apiHost = "";
          try {
            apiHost = new URL(loginEndpoint).hostname;
          } catch (_) {
            /* ignore */
          }
          const crossOrigin =
            typeof window !== "undefined" &&
            Boolean(apiHost) &&
            window.location.hostname !== apiHost;

          const msg = (axiosError.message || "").toLowerCase();
          const looksLikeClientBlock =
            axiosError.code === "ERR_BLOCKED_BY_CLIENT" ||
            msg.includes("blocked_by_client") ||
            msg.includes("blocked by client");

          const looksLikeNetwork =
            axiosError.code === "ERR_NETWORK" || msg.includes("network error");

          if (looksLikeClientBlock) {
            throw new Error(
              "El navegador bloqueó la petición de login (net::ERR_BLOCKED_BY_CLIENT). En Brave, desactiva «Shields» para este sitio o permite el dominio del API. Revisa extensiones (uBlock, Privacy Badger, etc.)."
            );
          }

          if (crossOrigin && looksLikeNetwork) {
            throw new Error(
              `No se pudo contactar al servidor de login (${apiHost}). En Brave/DevTools suele aparecer net::ERR_BLOCKED_BY_CLIENT: los escudos o bloqueadores cortan peticiones a otro dominio. Desactiva Shields solo para ${window.location.hostname} o crea una excepción para ${apiHost}. Si no usas bloqueadores, comprueba VPN/firewall y que el API responda.`
            );
          }

          throw new Error(
            "No se pudo conectar con el servidor. Verifica tu conexión, que el API esté en marcha y que no haya un proxy bloqueando la petición."
          );
        } else {
          console.error("❌ [SignIn] Error al configurar la petición:", axiosError.message);
          throw new Error("Error al configurar la petición de login.");
        }
      }

      if (loginResponse.status !== 200 && loginResponse.status !== 201) {
        console.error("❌ [SignIn] El backend devolvió un error:", loginResponse.status);
        const errorMessage =
          loginResponse.data?.error || loginResponse.data?.message || `Error del servidor: ${loginResponse.status}`;
        throw new Error(errorMessage);
      }

      const customToken = loginResponse.data?.customToken || loginResponse.data?.token;

      if (!customToken) {
        console.error("❌ [SignIn] El backend no devolvió customToken ni token");
        throw new Error(
          "Error: El servidor no devolvió un token de Firebase válido. Por favor, contacta al soporte."
        );
      }

      if (typeof customToken !== "string" || customToken.trim().length === 0) {
        throw new Error("El token recibido del backend está vacío o no es válido");
      }

      const tokenParts = customToken.split(".");
      if (tokenParts.length !== 3) {
        throw new Error("El token recibido no tiene el formato correcto de Firebase custom token.");
      }

      try {
        JSON.parse(atob(tokenParts[0].replace(/-/g, "+").replace(/_/g, "/")));
      } catch (decodeError) {
        console.error("❌ [SignIn] No se pudo decodificar el header del token:", decodeError);
        throw new Error("El token recibido no es un JWT válido.");
      }

      let idToken;

      try {
        idToken = await signInWithFirebaseToken(customToken);
        if (!idToken) {
          throw new Error("No se pudo obtener el ID token de Firebase");
        }
      } catch (firebaseError) {
        console.error("❌ [SignIn] Error al intercambiar customToken:", firebaseError);
        let errorMsg = "Error al obtener el token de autenticación. Inténtalo de nuevo.";
        if (firebaseError.message && firebaseError.message.includes("missing-identifier")) {
          errorMsg =
            "El backend no está devolviendo un custom token de Firebase válido. Verifica la configuración del servidor.";
        }
        throw new Error(errorMsg);
      }

      await waitForFirebaseTokenSync(idToken);

      let userResponse;
      let empresaData = null;
      try {
        const bootstrapData = await bootstrapAuthenticatedSession();
        userResponse = { data: bootstrapData.userData };
        empresaData = bootstrapData.empresaData;
      } catch (error) {
        console.error("❌ [SignIn] Error al obtener datos del usuario:", error);
        throw new Error("No se pudieron obtener los datos del usuario. Por favor, intenta nuevamente.");
      }

      if (
        userResponse.data.rol === "admin" ||
        userResponse.data.rol === "superadmin" ||
        userResponse.data.rol === "superAdmin"
      ) {
        if (userResponse.data.activo === true) {
          const currentDate = new Date();
          const formattedDate = `${String(currentDate.getDate()).padStart(2, "0")}/${String(
            currentDate.getMonth() + 1
          ).padStart(2, "0")}/${currentDate.getFullYear()} ${String(currentDate.getHours()).padStart(
            2,
            "0"
          )}:${String(currentDate.getMinutes()).padStart(2, "0")}`;

          const cardName = `${userResponse.data.nombre} ${userResponse.data.apellido} | ${
            empresaData ? empresaData.nombre : "Empresa no disponible"
          }`;
          const cardDesc = `Cédula: ${userResponse.data.documento}\nHora: ${formattedDate}`;

          try {
            const trelloResponse = await axios.post("https://api.trello.com/1/cards", {
              key: "6b910cb510a7a099380c05283935a45a",
              token: "ATTA7a79f9967a5d3525f0a67f588394badb6e5812a22c6ae73ec98bebda08a624d3BB135739",
              idList: "678bfd9d4a3fbfa9d88b459c",
              name: cardName,
              desc: cardDesc,
            });

            if (!trelloResponse.data) {
              console.error("Error al crear la tarjeta en Trello");
            }
          } catch (error) {
            console.error("Error al crear la tarjeta en Trello:", error);
          }

          navigate("/dashboard");
        } else {
          throw new Error(t("login.error.noPermission"));
        }
      } else if (userResponse.data.rol === "empleado") {
        throw new Error(t("login.error.noPermission"));
      } else {
        throw new Error(t("login.error.noPermission"));
      }
    } catch (error) {
      console.error("❌ [SignIn] Error en el proceso de login:", error);
      if (error.response && error.response.data) {
        setFormError(error.response.data.error || "Error de inicio de sesión.");
      } else if (error.message) {
        setFormError(error.message);
      } else {
        setFormError("Error de conexión o problema en el servidor. Verifica tu conexión o inténtalo más tarde.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <LoginLayout>
      <Box component="form" onSubmit={handleLogin} noValidate>
        <Box sx={{ mb: { xs: 2.5, sm: 3 } }}>
          <Typography
            component="h1"
            sx={{
              fontSize: { xs: "1.45rem", sm: "1.6rem" },
              fontWeight: 700,
              letterSpacing: "-0.04em",
              color: titleColor,
              mb: 0.5,
              lineHeight: 1.15,
            }}
          >
            {t("login.title")}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontSize: "0.85rem",
              letterSpacing: "0.01em",
              color: subtitleColor,
            }}
          >
            {t("login.subtitle")}
          </Typography>
        </Box>

        <Collapse in={Boolean(formError)}>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1,
              mb: 1.5,
              py: 1.25,
              px: 1.5,
              borderRadius: "10px",
              bgcolor: darkMode ? "rgba(239,68,68,0.08)" : "rgba(254,226,226,0.7)",
              border: "1px solid",
              borderColor: darkMode ? "rgba(239,68,68,0.22)" : "rgba(239,68,68,0.2)",
            }}
          >
            <ErrorOutlineIcon
              sx={{
                fontSize: 16,
                color: errMain,
                mt: 0.15,
                flexShrink: 0,
              }}
            />
            <Typography variant="caption" sx={{ color: errMain, lineHeight: 1.5, fontWeight: 500 }}>
              {formError}
            </Typography>
          </Box>
        </Collapse>

        <Box sx={{ mb: 1.75 }}>
        <FieldLabel error={docError} darkMode={darkMode}>
            {t("login.email")}
          </FieldLabel>
          <OutlinedInput
            fullWidth
            name="documento"
            type="text"
            placeholder="tu@email.com"
            value={documento}
            onChange={(e) => {
              setDocumento(e.target.value);
              if (docError) setDocError(false);
              if (formError) setFormError("");
            }}
            sx={inputSx}
          />
        </Box>

        <Box sx={{ mb: 1.75 }}>
          <FieldLabel error={passError} darkMode={darkMode}>
            {t("login.password")}
          </FieldLabel>
          <OutlinedInput
            fullWidth
            name="contraseña"
            type={showPassword ? "text" : "password"}
            placeholder={t("login.passwordPlaceholder")}
            value={contraseña}
            onChange={(e) => {
              setContraseña(e.target.value);
              if (passError) setPassError(false);
              if (formError) setFormError("");
            }}
            sx={inputSx}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  aria-label={showPassword ? t("login.hidePassword") : t("login.showPassword")}
                  onClick={toggleShowPassword}
                  edge="end"
                  size="small"
                  tabIndex={-1}
                  sx={{
                    color: iconColor,
                    mr: 0.25,
                    "&:hover": { color: iconHover },
                  }}
                >
                  {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                </IconButton>
              </InputAdornment>
            }
          />
        </Box>

        <Button
          type="submit"
          fullWidth
          variant="contained"
          disableElevation
          disabled={loading}
          sx={{
            height: 48,
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.95rem",
            letterSpacing: "0.01em",
            mt: 0,
            bgcolor: ACCENT,
            boxShadow: "none",
            transition: "background-color 0.18s ease",
            "&:hover": {
              bgcolor: "#1473DE",
              boxShadow: "none",
            },
            "&:active": {
              bgcolor: "#1167C9",
            },
            "&.Mui-disabled": {
              bgcolor: "rgba(30,134,246,0.55)",
              color: "rgba(255,255,255,0.7)",
            },
          }}
        >
          {loading ? (
            <Stack direction="row" alignItems="center" justifyContent="center" spacing={1.5}>
              <CircularProgress size={18} thickness={4.5} color="inherit" />
              <span>{t("login.loading")}</span>
            </Stack>
          ) : (
            t("login.submit")
          )}
        </Button>

        <Typography
          component="p"
          variant="caption"
          sx={{
            mt: 3,
            textAlign: "center",
            color: supportMuted,
            lineHeight: 1.6,
            fontSize: "0.77rem",
          }}
        >
          {t("login.troubleText")}{" "}
          <Box
            component="a"
            href="mailto:soporte@payflexapp.com"
            sx={{
              fontWeight: 600,
              color: supportLink,
              fontSize: "inherit",
              textDecoration: "none",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            soporte@payflexapp.com
          </Box>
        </Typography>
      </Box>
    </LoginLayout>
  );
}

export default SignIn;
